import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Container,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  KeyboardArrowDown,
  AccountCircle,
  Dashboard,
  Logout,
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const PublicHeader: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [featuresAnchor, setFeaturesAnchor] = useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  const navigationItems = [
    { label: 'Home', path: '/' },
    {
      label: 'Features',
      path: '/features',
      submenu: [
        { label: 'Content Organization', path: '/features#content-organization' },
        { label: 'Content Publishing', path: '/features#content-publishing' },
        { label: 'Team Collaboration', path: '/features#team-collaboration' },
        { label: 'Administration', path: '/features#administration' },
      ],
    },
    { label: 'Pricing', path: '/pricing' },
    { label: 'About', path: '/about' },
    { label: 'Help', path: '/help' },
  ];

  const handleFeaturesMouseEnter = (event: React.MouseEvent<HTMLButtonElement>) => {
    // On hover, show the menu anchored to the button
    setFeaturesAnchor(event.currentTarget);
  };

  const handleFeaturesClose = () => {
    setFeaturesAnchor(null);
  };

  const handleFeaturesTouchStart = (event: React.TouchEvent<HTMLButtonElement>) => {
    event.preventDefault();
    // On touch devices, toggle the menu open/closed
    if (featuresAnchor) {
      setFeaturesAnchor(null);
    } else {
      setFeaturesAnchor(event.currentTarget);
    }
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
    navigate('/');
  };

  const handleGoToApp = () => {
    handleUserMenuClose();
    navigate('/dashboard');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
    handleFeaturesClose();
    handleUserMenuClose();
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <AppBar
      position="sticky"
      color="default"
      elevation={1}
      sx={{
        backgroundColor: 'background.paper',
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar sx={{ minHeight: { xs: 64, md: 72 }, px: { xs: 0, sm: 0 } }}>
          {/* Logo */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              mr: 4,
            }}
          >
            <Box
              component="img"
              src="/assets/tier-one-content_icon_dynamic-layers_128.png"
              alt="Tier One Content Logo"
              sx={{
                width: 32,
                height: 32,
                mr: 0.5,
              }}
            />
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Tier One Content
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              {navigationItems.map((item) => (
                <React.Fragment key={item.label}>
                  {item.submenu ? (
                    <>
                      <Button
                        component={Link as any}
                        to={item.path}
                        onMouseEnter={handleFeaturesMouseEnter}
                        onTouchStart={handleFeaturesTouchStart}
                        endIcon={<KeyboardArrowDown />}
                        sx={{
                          mx: 1,
                          color: isActive(item.path) ? 'primary.main' : 'text.primary',
                          fontWeight: isActive(item.path) ? 600 : 400,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        {item.label}
                      </Button>
                      <Menu
                        anchorEl={featuresAnchor}
                        open={Boolean(featuresAnchor)}
                        onClose={handleFeaturesClose}
                        MenuListProps={{
                          onMouseEnter: () => setFeaturesAnchor(featuresAnchor),
                          onMouseLeave: handleFeaturesClose,
                        }}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        slotProps={{
                          paper: {
                            onMouseEnter: () => setFeaturesAnchor(featuresAnchor),
                            onMouseLeave: handleFeaturesClose,
                          },
                        }}
                      >
                        {item.submenu.map((subItem) => (
                          <MenuItem
                            key={subItem.label}
                            onClick={() => handleNavigation(subItem.path)}
                          >
                            {subItem.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  ) : (
                    <Button
                      component={Link}
                      to={item.path}
                      sx={{
                        mx: 1,
                        color: isActive(item.path) ? 'primary.main' : 'text.primary',
                        fontWeight: isActive(item.path) ? 600 : 400,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  )}
                </React.Fragment>
              ))}
            </Box>
          )}

          {/* Spacer */}
          {!isMobile && <Box sx={{ flexGrow: 1 }} />}

          {/* Auth Buttons / User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isAuthenticated && user ? (
              // Authenticated user menu
              <>
                <Typography
                  variant="body2"
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    mr: 1,
                    color: 'text.secondary',
                  }}
                >
                  Welcome, {user.name || user.email}
                </Typography>
                <IconButton
                  onClick={handleUserMenuClick}
                  sx={{
                    p: 0,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: 'primary.main',
                      fontSize: '0.875rem',
                    }}
                  >
                    {(user.name || user.email).charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem onClick={handleGoToApp}>
                    <Dashboard sx={{ mr: 1 }} />
                    Go to App
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <Logout sx={{ mr: 1 }} />
                    Sign Out
                  </MenuItem>
                </Menu>
              </>
            ) : (
              // Unauthenticated user buttons
              <>
                <Button
                  component={Link}
                  to="/login"
                  variant="text"
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                  }}
                >
                  Sign In
                </Button>
                <Button
                  component={Link}
                  to="/signup"
                  variant="contained"
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                    },
                  }}
                >
                  Get Started
                </Button>
              </>
            )}
          </Box>

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ ml: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: { width: 280 },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setMobileMenuOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List>
          {navigationItems.map((item) => (
            <React.Fragment key={item.label}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  selected={isActive(item.path)}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
              {item.submenu && (
                <List sx={{ pl: 2 }}>
                  {item.submenu.map((subItem) => (
                    <ListItem key={subItem.label} disablePadding>
                      <ListItemButton
                        onClick={() => handleNavigation(subItem.path)}
                      >
                        <ListItemText
                          primary={subItem.label}
                          primaryTypographyProps={{
                            variant: 'body2',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </React.Fragment>
          ))}
        </List>
        <Divider />
        <Box sx={{ p: 2 }}>
          {isAuthenticated && user ? (
            // Authenticated mobile menu
            <>
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: 'primary.main',
                    mx: 'auto',
                    mb: 1,
                  }}
                >
                  {(user.name || user.email).charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="body2" color="text.secondary">
                  {user.name || user.email}
                </Typography>
              </Box>
              <Button
                fullWidth
                onClick={() => {
                  handleGoToApp();
                  setMobileMenuOpen(false);
                }}
                variant="contained"
                startIcon={<Dashboard />}
                sx={{ mb: 1 }}
              >
                Go to App
              </Button>
              <Button
                fullWidth
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                variant="outlined"
                startIcon={<Logout />}
              >
                Sign Out
              </Button>
            </>
          ) : (
            // Unauthenticated mobile menu
            <>
              <Button
                fullWidth
                component={Link}
                to="/login"
                variant="outlined"
                sx={{ mb: 1 }}
                onClick={() => setMobileMenuOpen(false)}
              >
                Sign In
              </Button>
              <Button
                fullWidth
                component={Link}
                to="/signup"
                variant="contained"
                onClick={() => setMobileMenuOpen(false)}
              >
                Get Started
              </Button>
            </>
          )}
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default PublicHeader;